<script lang="ts">
	/**
	 * A refund's credit note, on the three support screens that list refunds (Design §4.6).
	 *
	 * One component rather than three copies, because the state it renders is **three answers, not
	 * two**, and getting that wrong is invisible:
	 *
	 * - a credit note exists → its number, linking to the document support can quote back;
	 * - none exists **and the store issues its own documents** → an Issue button, because the refund
	 *   path swallows its own failure (it has to: the money has already moved by then, and answering
	 *   a completed refund with an error invites a second refund), so this is the only place the gap
	 *   is visible and the only place it can be closed;
	 * - none exists **because the payment provider issues its own** → nothing to do, and no button.
	 *
	 * The third is why the button is not simply rendered whenever the note is null. The screen
	 * cannot tell the second case from the third on its own — the toggle is per payment provider —
	 * so it offers the button and lets the server refuse by name (`creditNote.providerIssued`),
	 * which is rendered as the sentence it is rather than as a failure. A button that silently did
	 * nothing, or an error toast about a store that is working exactly as configured, are the two
	 * worse options.
	 *
	 * A refund that moved no money gets neither: `failed` and `canceled` refunded nothing, and a
	 * credit note for money that never went back is a document claiming a credit the customer does
	 * not have.
	 */

	import { createEventDispatcher } from 'svelte';
	import { Button } from 'flowbite-svelte';
	import CreditNoteLink from './CreditNoteLink.svelte';
	import { issueCreditNote } from '../AppNetwork';
	import { toastError, toastSuccess } from '../stores/toast';
	import type { StoreRefund } from '../AppTypes';

	export let refund: StoreRefund;

	const dispatch = createEventDispatcher<{ issued: string }>();

	/** Per row, so one refund's spinner does not disable the others. */
	let issuing: boolean = false;

	/** The statuses that moved, or may still move, money — the same pair the server counts. */
	$: movedMoney = refund.status === 'pending' || refund.status === 'succeeded';

	/**
	 * Whether there is a credit note to link to.
	 *
	 * A **non-empty-string test on the number**, never `creditNote !== null`, and this component
	 * shipped once with the latter: `undefined` passes `!== null`, which is what a payload written
	 * before the field existed reads as — and the row then dereferenced a document that was not
	 * there and took the whole screen down with it. The same rule `hasIssuedInvoiceDocument` follows,
	 * for the same reason, on the same kind of column.
	 */
	$: issued =
		refund.creditNote !== null &&
		refund.creditNote !== undefined &&
		typeof refund.creditNote.documentNumber === 'string' &&
		refund.creditNote.documentNumber !== '';

	async function fnIssue(): Promise<void> {
		issuing = true;
		try {
			const creditNote = await issueCreditNote(refund.uuid);
			toastSuccess(`Credit note ${creditNote.documentNumber} issued.`);
			// The parent reloads rather than this patching its own prop: the refund arrived inside a
			// payload assembled server-side in one call, and patching one field of it would leave
			// the rest of the screen describing a moment before the issue.
			dispatch('issued', refund.uuid);
		} catch (error) {
			toastError('The credit note could not be issued.', (error as Error).message);
		} finally {
			issuing = false;
		}
	}
</script>

{#if issued && refund.creditNote}
	<CreditNoteLink
		uuid={refund.creditNote.uuid}
		documentNumber={refund.creditNote.documentNumber}
		label={refund.creditNote.documentNumber}
	/>
{:else if movedMoney}
	<Button size="xs" color="alternative" disabled={issuing} on:click={() => void fnIssue()}>
		{issuing ? 'Issuing…' : 'Issue credit note'}
	</Button>
{:else}
	<span class="text-default">—</span>
{/if}
